import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Menu, X, LogOut, Settings, Package, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { MegaMenuColumns } from './MegaMenuPanel';
import RotatingMascot from './RotatingMascot';
import NotificationBell from './NotificationBell';
import { optimizedImage } from '../utils/productImage';

const Header = ({ cartCount = 0, wishlistCount = 0 }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [expandedMobileCat, setExpandedMobileCat] = useState(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const [activeL1, setActiveL1] = useState(null);
  const megaCloseRef = useRef(null);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  // Load navigation categories from database
  useEffect(() => {
    const loadNavCategories = async () => {
      try {
        const response = await api.get('/categories');
        const cats = response.data || [];
        setAllCategories(cats);
        // Categoriile marcate explicit „în meniul principal"; dacă nu există niciuna,
        // păstrăm comportamentul vechi (primele 6 categorii principale după ordine)
      } catch (error) {
        console.error('Error loading nav categories:', error);
      }
    };
    loadNavCategories();
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (!event.target.closest('.search-box')) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  // Live search with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length >= 2) {
      setSearchLoading(true);
      setShowSearchDropdown(true);
      
      // Debounce search by 300ms
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          // Include out of stock products in search results
          const response = await api.get(`/products?search=${encodeURIComponent(query)}&limit=8&include_out_of_stock=true`);
          const products = response.data?.products || response.data || [];
          setSearchResults(products.slice(0, 8));
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchDropdown(false);
      navigate(`/catalog?search=${encodeURIComponent(searchQuery)}`);
      setMobileMenuOpen(false);
    }
  };

  const handleProductClick = (productId) => {
    setShowSearchDropdown(false);
    setSearchQuery('');
    navigate(`/product/${productId}`);
  };

  // Meniul orizontal: doar scurtăturile cerute de client (restul categoriilor sunt în „Toate Categoriile”)
  const categories = [
    { name: 'Oferte', path: '/catalog?filter=oferte', special: true },
    { name: 'Noutăți', path: '/catalog?filter=noutati' },
    { name: 'RCA', path: '/rca', insurance: true },
    { name: 'Bebe', path: '/bebelusi' },
    { name: 'Parfumuri', path: '/catalog?category=parfumuri' },
  ];

  // All active L1 categories for the mega-menu left rail
  const byOrder = (a, b) => (a.order || 0) - (b.order || 0) || (a.name || '').localeCompare(b.name || '');
  const megaMainCats = allCategories
    .filter(cat => !cat.parentId && cat.isActive !== false)
    .sort(byOrder);

  const openMega = () => {
    if (megaCloseRef.current) { clearTimeout(megaCloseRef.current); megaCloseRef.current = null; }
    if (!activeL1 && megaMainCats.length > 0) setActiveL1(megaMainCats[0]);
    setMegaOpen(true);
  };
  const scheduleMegaClose = () => {
    megaCloseRef.current = setTimeout(() => setMegaOpen(false), 250);
  };
  const closeMega = () => {
    if (megaCloseRef.current) { clearTimeout(megaCloseRef.current); megaCloseRef.current = null; }
    setMegaOpen(false);
  };

  // Reusable search box (form + live results dropdown), used on both desktop and mobile
  const renderSearch = () => (
    <>
      <form onSubmit={handleSearch}>
        <div className="relative w-full">
          <Input
            type="text"
            placeholder="Caută produse..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchQuery.length >= 2 && setShowSearchDropdown(true)}
            className="w-full pl-4 pr-12 py-3 lg:py-6 border-2 border-gray-200 rounded-2xl !text-base"
            data-testid="search-input"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-green-600 hover:bg-green-700 rounded-xl px-3 lg:px-6 py-2 lg:py-3"
            data-testid="search-submit"
          >
            {searchLoading ? (
              <Loader2 className="h-4 w-4 lg:h-5 lg:w-5 text-white animate-spin" />
            ) : (
              <Search className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
            )}
          </button>
        </div>
      </form>

      {showSearchDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
          {searchLoading ? (
            <div className="p-4 text-center text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Se caută...
            </div>
          ) : searchResults.length > 0 ? (
            <>
              {searchResults.map((product) => (
                <div
                  key={product._id || product.id}
                  onClick={() => handleProductClick(product._id || product.id || product.slug)}
                  className="flex items-center p-3 hover:bg-green-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="w-16 h-16 flex-shrink-0 mr-3">
                    <img
                      src={optimizedImage(product.images?.[0] || product.image, 120)}
                      alt={product.name}
                      className="w-full h-full object-contain rounded-lg bg-gray-50"
                      onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-product.svg'; }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">{product.name}</p>
                    <p className="text-xs text-gray-500 truncate">{product.category}</p>
                    <div className="flex items-center mt-1">
                      <span className="font-bold text-green-600">{product.price?.toFixed(2)} Lei</span>
                      {product.oldPrice > product.price && (
                        <span className="text-xs text-gray-400 line-through ml-2">
                          {Number(product.oldPrice).toFixed(2)} Lei
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div
                onClick={handleSearch}
                className="p-3 text-center text-green-600 hover:bg-green-50 cursor-pointer font-medium"
              >
                Vezi toate rezultatele pentru "{searchQuery}"
              </div>
            </>
          ) : searchQuery.length >= 2 ? (
            <div className="p-4 text-center text-gray-500">
              Nu s-au găsit produse pentru "{searchQuery}"
            </div>
          ) : null}
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs sm:text-sm">
          <span className="hidden sm:inline">Transport gratuit pentru comenzi peste 400 Lei • </span>
          Returnare în 14 zile
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between py-2 sm:py-4 gap-2 sm:gap-4 w-full">
            {/* Left side: Menu button + Logo */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden flex h-11 w-11 items-center justify-center rounded-lg hover:bg-gray-100"
                aria-label="Menu"
              >
                <Menu className="h-6 w-6 text-gray-700" />
              </button>

              {/* Logo */}
              <Link to="/">
                <div className="bg-gradient-to-br from-green-600 to-green-500 text-white font-bold text-lg sm:text-xl lg:text-2xl px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl shadow-lg">
                  R32
                </div>
              </Link>
            </div>

            {/* Center: Desktop mascot + slogan */}
            <div className="hidden md:flex items-center space-x-2 lg:space-x-3">
              <RotatingMascot className="w-12 lg:w-24 h-12 lg:h-24" />
              <div className="hidden lg:block" style={{ perspective: '400px', perspectiveOrigin: 'center bottom' }}>
                <p className="text-base leading-tight text-green-600" style={{ 
                  fontFamily: "'Star Jedi', sans-serif",
                  transform: 'rotateX(30deg)',
                  transformStyle: 'preserve-3d',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '0.1em'
                }}>
                  simple<br />as<br />you
                </p>
              </div>
            </div>

            {/* Mobile: Druid on right side */}
            <div className="md:hidden flex-shrink-0">
              <RotatingMascot className="w-10 h-10" />
            </div>

            {/* Desktop Search Bar with Live Results */}
            <div className="search-box hidden md:block flex-1 max-w-2xl relative">
              {renderSearch()}
            </div>

            {/* Right Icons */}
            <div className="flex items-center space-x-1 sm:space-x-4 min-w-0 shrink">
              <NotificationBell />
              <Link to="/wishlist" className="relative hidden min-[360px]:block">
                <button aria-label="Favorite" className="relative flex h-11 w-11 items-center justify-center rounded-xl hover:bg-gray-100">
                  <Heart className="h-5 w-5 sm:h-6 sm:w-6" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center font-bold">
                      {wishlistCount}
                    </span>
                  )}
                </button>
              </Link>

              <Link to="/cart" className="relative">
                <button aria-label="Coș" className="relative flex h-11 w-11 items-center justify-center rounded-xl hover:bg-gray-100">
                  <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center font-bold">
                      {cartCount}
                    </span>
                  )}
                </button>
              </Link>

              {isAuthenticated && user ? (
                <div className="relative" ref={userMenuRef}>
                  <button 
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex min-h-[44px] items-center space-x-2 rounded-xl px-2 hover:bg-gray-100"
                  >
                    <User className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                    <span className="hidden lg:inline text-sm font-medium">{user?.name}</span>
                    <ChevronDown className="hidden lg:inline h-4 w-4 text-gray-400" />
                  </button>
                  
                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="font-semibold text-sm">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <Link 
                        to="/account" 
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-green-50"
                      >
                        <User className="h-4 w-4 mr-3 text-gray-400" />
                        Contul meu
                      </Link>
                      <Link 
                        to="/orders" 
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-green-50"
                      >
                        <Package className="h-4 w-4 mr-3 text-gray-400" />
                        Comenzile mele
                      </Link>
                      <Link 
                        to="/addresses" 
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-green-50"
                      >
                        <Settings className="h-4 w-4 mr-3 text-gray-400" />
                        Setări cont
                      </Link>
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <button 
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Deconectare
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login">
                  <button aria-label="Login" className="flex min-h-[44px] items-center justify-center rounded-xl bg-green-600 px-3 text-sm font-medium text-white hover:bg-green-700 sm:px-4">
                    <User className="h-5 w-5 min-[360px]:hidden" />
                    <span className="hidden min-[360px]:inline">Login</span>
                  </button>
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Search Bar (below header) - full width with live results */}
          <div className="search-box md:hidden pb-3 relative">
            {renderSearch()}
          </div>
        </div>
      </header>

      {/* Desktop Navigation with eMAG-style Mega Menu */}
      <nav className="hidden lg:block bg-white border-b border-gray-200 relative z-40">
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="flex flex-wrap items-center gap-1 py-3">
            {/* Toate Categoriile - mega menu trigger */}
            <div
              onMouseEnter={openMega}
              onMouseLeave={scheduleMegaClose}
            >
              <Link to="/catalog" onClick={closeMega}>
                <button
                  className={`rounded-xl text-sm px-4 py-2 font-semibold transition-colors flex items-center ${
                    megaOpen ? 'bg-green-600 text-white' : 'hover:bg-gray-100'
                  }`}
                  data-testid="megamenu-trigger"
                >
                  <Menu className="h-4 w-4 mr-2" />
                  Toate Categoriile
                  <ChevronDown className="h-4 w-4 ml-2" />
                </button>
              </Link>
            </div>

            {categories.map((cat, index) => (
              <Link key={index} to={cat.path}>
                <button
                  data-testid={cat.insurance ? 'nav-rca-btn' : undefined}
                  className={`rounded-xl text-sm px-4 py-2 transition-colors whitespace-nowrap ${
                    cat.insurance
                      ? 'text-green-700 font-semibold hover:bg-green-50'
                      : `hover:bg-gray-100 ${cat.special ? 'text-red-600 font-semibold' : ''}`
                  }`}
                >
                  {cat.name}
                </button>
              </Link>
            ))}
          </div>

          {/* Mega Menu Panel - full container width, fixed height, no vertical stretch */}
          {megaOpen && megaMainCats.length > 0 && (
            <div
              className="absolute left-4 right-4 top-full bg-white border border-gray-200 rounded-b-2xl shadow-2xl flex overflow-hidden"
              style={{ height: 'min(480px, calc(100vh - 220px))', zIndex: 60 }}
              onMouseEnter={openMega}
              onMouseLeave={scheduleMegaClose}
              data-testid="category-megamenu"
            >
              {/* Left rail: L1 categories */}
              <div className="w-72 flex-shrink-0 border-r border-gray-100 bg-gray-50 overflow-y-auto py-2">
                {megaMainCats.map((cat) => (
                  <Link
                    key={cat._id}
                    to={`/catalog?category=${cat.slug}`}
                    onClick={closeMega}
                    onMouseEnter={() => setActiveL1(cat)}
                    className={`flex items-center justify-between px-5 py-2.5 text-sm transition-colors ${
                      activeL1?._id === cat._id
                        ? 'bg-white text-green-600 font-semibold border-l-4 border-green-600'
                        : 'text-gray-700 hover:bg-white hover:text-green-600 border-l-4 border-transparent'
                    }`}
                    data-testid={`megamenu-l1-${cat.slug}`}
                  >
                    <span className="truncate">{cat.name}</span>
                    <ChevronDown className="h-4 w-4 -rotate-90 flex-shrink-0 text-gray-400" />
                  </Link>
                ))}
              </div>

              {/* Right panel: L2 headers + L3 links in columns, fitted (no scrollbar) */}
              <div className="flex-1 px-8 py-5 overflow-hidden" data-testid="megamenu-content">
                {activeL1 && (
                  <>
                    <Link
                      to={`/catalog?category=${activeL1.slug}`}
                      onClick={closeMega}
                      className="inline-block text-base font-bold text-green-600 hover:underline mb-3"
                      data-testid="megamenu-active-l1-title"
                    >
                      {activeL1.name} →
                    </Link>
                    <MegaMenuColumns
                      category={activeL1}
                      categories={allCategories}
                      colCount={4}
                      heightPx={370}
                      onNavigate={closeMega}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85%] bg-white shadow-2xl overflow-y-auto">
            {/* Header */}
            <div className="bg-green-600 text-white p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold">Meniu</h2>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* User Info */}
            {isAuthenticated && user && (
              <div className="bg-green-50 p-4 border-b">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{user.name}</p>
                    <p className="text-xs text-gray-600">{user.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Arborele complet de categorii (ca meniul vertical de pe desktop) */}
            <div className="p-2">
              <p className="px-4 pt-2 pb-2 text-xs font-semibold text-gray-500 uppercase">Toate Categoriile</p>
              <Link to="/catalog" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-cat-all">
                <div className="px-4 py-3 hover:bg-gray-100 flex items-center justify-between">
                  <span className="text-sm font-medium text-green-700">Vezi tot catalogul</span>
                  <span className="text-gray-400">›</span>
                </div>
              </Link>
              {megaMainCats.map((cat) => {
                const children = allCategories
                  .filter(c => String(c.parentId) === String(cat._id) && c.isActive !== false)
                  .sort(byOrder);
                const open = expandedMobileCat === String(cat._id);
                return (
                  <div key={cat._id} className="border-b border-gray-100 last:border-0">
                    <div className="flex items-center justify-between">
                      <Link to={`/catalog?category=${cat.slug}`} onClick={() => setMobileMenuOpen(false)}
                        className="flex-1 px-4 py-3 text-sm text-gray-900 hover:bg-gray-100" data-testid={`mobile-cat-${cat.slug}`}>
                        {cat.name}
                      </Link>
                      {children.length > 0 && (
                        <button onClick={() => setExpandedMobileCat(open ? null : String(cat._id))}
                          className="px-4 py-3 text-gray-400" aria-label={`Subcategorii ${cat.name}`}
                          data-testid={`mobile-cat-toggle-${cat.slug}`}>
                          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {open && children.length > 0 && (
                      <div className="bg-gray-50 pb-1" data-testid={`mobile-subcats-${cat.slug}`}>
                        {children.map((sub) => (
                          <Link key={sub._id} to={`/catalog?category=${sub.slug}`} onClick={() => setMobileMenuOpen(false)}>
                            <div className="px-8 py-2.5 text-sm text-gray-700 hover:bg-gray-100">{sub.name}</div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom Actions */}
            <div className="border-t p-4 space-y-2">
              {!isAuthenticated && (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 font-semibold">
                    Autentificare
                  </button>
                </Link>
              )}
              {isAuthenticated && (
                <button 
                  onClick={() => { logout(); setMobileMenuOpen(false); navigate('/'); }}
                  className="w-full border-2 border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl py-3 font-semibold"
                >
                  Deconectare
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
